import { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, selectTriggerClassName,
} from "@/components/ui/select";
import {
  BottomSheet, BottomSheetContent, BottomSheetTitle, BottomSheetTrigger,
} from "@/components/ui/bottom-sheet";
import { useIsTouch } from "@/lib/useMediaQuery";
import { cn } from "@/lib/utils";

/**
 * Pick one value from a list too long for chips.
 *
 * A mouse gets the Radix dropdown. A finger gets a bottom sheet instead,
 * because the dropdown fails on a phone in three ways: it closes itself on
 * window resize, which is exactly what the keyboard hiding does, so the first
 * tap after typing is lost; it is only as wide as its trigger and as tall as
 * the space left; and its rows are sized for a cursor. The sheet spans the
 * screen, sits in thumb reach and gives every row a full touch target.
 *
 * Options are data — `{ value, label, icon?, testid? }` — so callers describe
 * the list once and both presentations render it.
 */
export function OptionSelect({ className, ...props }) {
  const touch = useIsTouch();
  const Presentation = touch ? OptionSheet : OptionDropdown;
  return <Presentation className={className} {...props} />;
}

function OptionDropdown({
  options, value, onValueChange, placeholder, title, className, ...triggerProps
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger aria-label={title} className={className} {...triggerProps}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-72 border-border bg-card">
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value} data-testid={option.testid}>
            <OptionLabel option={option} />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function OptionSheet({
  options, value, onValueChange, placeholder, title, className, ...triggerProps
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.value === value);

  const choose = (nextValue) => {
    onValueChange(nextValue);
    setOpen(false);
  };

  return (
    <BottomSheet open={open} onOpenChange={setOpen}>
      <BottomSheetTrigger
        type="button"
        aria-label={title}
        data-placeholder={selected ? undefined : ""}
        className={cn(selectTriggerClassName, className)}
        {...triggerProps}
      >
        <span>{selected ? <OptionLabel option={selected} /> : placeholder}</span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
      </BottomSheetTrigger>
      <BottomSheetContent>
        <BottomSheetTitle>{title}</BottomSheetTitle>
        <OptionList options={options} value={value} onChoose={choose} />
      </BottomSheetContent>
    </BottomSheet>
  );
}

function OptionList({ options, value, onChoose }) {
  return (
    <div role="listbox" className="overflow-y-auto overscroll-contain px-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="option"
            aria-selected={selected}
            data-testid={option.testid}
            onClick={() => onChoose(option.value)}
            className={cn(
              "flex min-h-[3.25rem] w-full items-center justify-between gap-3 rounded-md px-3 text-left text-base active:bg-secondary",
              selected && "bg-secondary/60 font-semibold",
            )}
          >
            <OptionLabel option={option} />
            {selected && <Check className="h-5 w-5 shrink-0 text-income" />}
          </button>
        );
      })}
    </div>
  );
}

function OptionLabel({ option }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      {option.icon}
      <span className="truncate">{option.label}</span>
    </span>
  );
}
